import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    BookmarkIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    PhoneIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline";
import {
    BookmarkIcon as BookmarkSolidIcon,
    StarIcon as StarSolidIcon,
} from "@heroicons/react/24/solid";
import { IEstablishment } from "../../interfaces/establishment";
import {
    saveEstablishment,
    viewEstablishment,
} from "../../interfaces/establishments";

interface SignalCardProps {
    signal: IEstablishment;
    onSaveToggle?: (id: string, saved: boolean) => void;
    activeSignalTypes?: string[];
    /** When set (founder page), show the person whose name was searched for instead of
     *  peoples[0] — an establishment matches on ANY of its dirigeants. */
    focusPersonName?: string;
}

// Signal-type tone map — mirrors the semantic mapping used across the signals feature
// (opening = success, owner change = info, hygiene control = warning, closing = danger).
const SIGNAL_TYPE_ICON: Record<string, string> = {
    'restaurant_opening': '🏪',
    'restaurant_owner_change': '👤',
    'multi_site': '📍',
    'restaurant_closing': '🔒',
    'hygiene_control': '🏥',
    'franchise_signing': '🤝',
    'network_expansion': '🌐',
};
const SIGNAL_TYPE_LABEL: Record<string, string> = {
    'restaurant_opening': 'Ouverture',
    'restaurant_owner_change': 'Changement gérant',
    'multi_site': 'Multi-site',
    'restaurant_closing': 'Fermeture',
    'hygiene_control': 'Contrôle sanitaire',
    'franchise_signing': 'Franchise',
    'network_expansion': 'Expansion',
};

// Not rendered as pills: the opening one repeats the folder/date column, multi-site repeats
// the establishment count, and BODACC is an internal source tag with no user-facing label.
// Mirrors SignalTable.
const HIDDEN_SIGNAL_BADGES = new Set(["restaurant_opening", "multi_site", "bodacc_announcement"]);

export default function SignalCard({ signal: establishment, onSaveToggle, activeSignalTypes, focusPersonName }: SignalCardProps) {
    const navigate = useNavigate();
    const userId = localStorage.getItem("userId") || "";
    const savedBy: string[] = (establishment as any).savedBy || [];
    const viewedBy: string[] = (establishment as any).viewedBy || [];

    const [isSaved, setIsSaved] = useState(savedBy.includes(userId));
    const [savingInProgress, setSavingInProgress] = useState(false);
    const [copiedPhone, setCopiedPhone] = useState(false);
    const [copiedEmail, setCopiedEmail] = useState(false);

    // Derived contact data from the focused person's enrichments, falling back to peoples[0]
    const primaryPerson = (() => {
        const people: any[] = (establishment as any).peoples || [];
        if (focusPersonName) {
            const wanted = focusPersonName.trim().toLowerCase();
            const match = people.find((p: any) => (p?.name || "").trim().toLowerCase() === wanted);
            if (match) return match;
        }
        return people[0];
    })();
    const enrichments: any[] = (primaryPerson as any)?.enrichments || [];
    const personName: string | undefined = (primaryPerson as any)?.name;
    const phoneEnrichment = enrichments.find((e: any) => e.type === "phone" && (e.status === "done" || e.status === "approved"));
    const emailEnrichment = enrichments.find((e: any) => (e.type === "email" || e.type === "email_company") && (e.status === "done" || e.status === "approved"));
    const linkedinEnrichment = enrichments.find((e: any) => e.type === "linkedin" && (e.status === "done" || e.status === "approved"));
    const phone: string | undefined = establishment.phone || phoneEnrichment?.data?.phone;
    const email: string | undefined = establishment.email || emailEnrichment?.data?.email;
    const linkedin: string | undefined = linkedinEnrichment?.data?.url;

    // Ratings from google (first source_summary entry)
    const googleRating = establishment.ratings?.source_summary?.[0];
    const allSignals: any[] = (establishment as any).signals || [];
    const relevantSignal: any = (() => {
        if (activeSignalTypes && activeSignalTypes.length > 0) {
            const relevant = allSignals.filter(s => activeSignalTypes.includes(s.type));
            if (relevant.length > 0) {
                return relevant.reduce((latest: any, s: any) =>
                    !latest || new Date(s.date) > new Date(latest.date) ? s : latest
                );
            }
        }
        return allSignals[0];
    })();
    const signalDate = relevantSignal?.date;
    const daysUntilOpening: number | null = (() => {
        if (relevantSignal?.type !== "restaurant_opening" || !signalDate) return null;
        const target = new Date(signalDate); target.setHours(0, 0, 0, 0);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
        return diff >= 0 ? diff : null;
    })();
    const franchise: string | undefined = (establishment as any).franchise;
    const categories: string[] = (establishment as any).categories || [];
    const subCategories: string[] = (establishment as any).subCategories || [];

    const isViewed = viewedBy.includes(userId);
    const hasPerson = !!personName;
    const enrichmentLevel = hasPerson ? "Personne" : "Société";

    const handleSave = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (savingInProgress) return;
        try {
            setSavingInProgress(true);
            const result = await saveEstablishment(establishment._id);
            setIsSaved(result.saved);
            if (onSaveToggle) onSaveToggle(establishment._id, result.saved);
        } catch {
            console.error("Failed to save establishment");
        } finally {
            setSavingInProgress(false);
        }
    };

    const handleCardClick = async () => {
        try { await viewEstablishment(establishment._id); } catch { /* ignore */ }
        navigate(`/establishments/${establishment._id}`);
    };


    const handleCopyPhone = (e: React.MouseEvent, value: string) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
    };

    const handleCopyEmail = (e: React.MouseEvent, value: string) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString("fr-FR", { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const formattedDate = formatDate(signalDate);
    const isRecent = formattedDate && (
        formattedDate.includes('m') || formattedDate.includes('h') || formattedDate.includes('instant')
    ) && !formattedDate.includes('sem');

    return (
        <div
            onClick={handleCardClick}
            className={`
                relative bg-surface border rounded-2xl p-4 hover:border-border-strong cursor-pointer
                transition-colors duration-150 flex flex-col h-full
                ${!isViewed ? 'border-accent/30 bg-accent-subtle/20' : 'border-border'}
            `}
        >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-ink mb-1.5 line-clamp-2">
                        {establishment.name}
                    </h3>

                    <div className="flex flex-col gap-1 mb-2">
                        {/* Name + location */}
                        <div className="flex items-center gap-2 text-[11px] text-ink-muted">
                            <BuildingOfficeIcon className="h-3 w-3 flex-shrink-0" />
                            <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/establishments/${establishment._id}`); }}
                                className="font-medium truncate hover:text-ink hover:underline transition-colors duration-150"
                            >
                                {establishment.name}
                            </button>
                            {establishment.address?.city && (
                                <>
                                    <span className="text-ink-faint">•</span>
                                    <MapPinIcon className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate">{establishment.address.city}</span>
                                </>
                            )}
                        </div>

                        {/* Primary person */}
                        {personName && (
                            <div className="flex items-center gap-2 text-[11px] text-ink-muted">
                                <svg className="h-3 w-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span className="font-medium">{personName}</span>
                            </div>
                        )}

                        {/* Signal Types Display */}
                        <div className="flex flex-wrap gap-1">
                            {(() => {
                                const signals = (establishment as any).signals || [];
                                if (!Array.isArray(signals) || signals.length === 0) {
                                    return null;
                                }

                                // Get unique signal types
                                const uniqueTypes = [...new Set(signals.map((s: any) => s.type).filter(Boolean) as string[])]
                                    .filter(t => !HIDDEN_SIGNAL_BADGES.has(t));

                                return uniqueTypes.map((type: string) => {
                                    const icon = SIGNAL_TYPE_ICON[type] || '📡';
                                    const label = SIGNAL_TYPE_LABEL[type] || type;

                                    return (
                                        <span
                                            key={type}
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-surface-sunken rounded-sm text-[10px] font-medium text-ink-muted"
                                        >
                                            <span>{icon}</span>
                                            <span>{label}</span>
                                        </span>
                                    );
                                });
                            })()}
                        </div>

                        {/* Google rating */}
                        {(googleRating?.rating_avg || googleRating?.rating_count) && (
                            <div className="flex items-center gap-2 text-[11px]">
                                {googleRating.rating_avg && (
                                    <div className="flex items-center gap-1 text-warning font-medium">
                                        <StarSolidIcon className="h-3 w-3" />
                                        <span>{googleRating.rating_avg}</span>
                                    </div>
                                )}
                                {googleRating.rating_count && (
                                    <span className="text-ink-faint">({googleRating.rating_count} avis)</span>
                                )}
                                {googleRating.url && (
                                    <a
                                        href={googleRating.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-ink-muted hover:text-ink hover:underline ml-1 transition-colors duration-150"
                                    >
                                        Maps
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Contact quick actions */}
                        {(phone || email || linkedin) && (
                            <div className="flex items-center gap-2 text-[11px] text-ink-muted mt-0.5">
                                {phone && (
                                    <button
                                        onClick={(e) => handleCopyPhone(e, phone)}
                                        className="flex items-center gap-1 hover:text-ink transition-colors duration-150"
                                        title={copiedPhone ? "Copié !" : phone}
                                    >
                                        <PhoneIcon className="h-3 w-3" />
                                        <span className="truncate max-w-[80px]">{copiedPhone ? "Copié !" : phone}</span>
                                    </button>
                                )}
                                {email && (
                                    <button
                                        onClick={(e) => handleCopyEmail(e, email)}
                                        className="flex items-center gap-1 hover:text-ink transition-colors duration-150"
                                        title={copiedEmail ? "Copié !" : email}
                                    >
                                        <EnvelopeIcon className="h-3 w-3" />
                                        <span className="truncate max-w-[80px]">{copiedEmail ? "Copié !" : email}</span>
                                    </button>
                                )}
                                {linkedin && (
                                    <a
                                        href={linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex items-center gap-1 text-[#0A66C2] hover:text-[#004182] transition-colors duration-150"
                                    >
                                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                        </svg>
                                        <span>LinkedIn</span>
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Bookmark */}
                <button
                    onClick={handleSave}
                    disabled={savingInProgress}
                    className="ml-2 p-1.5 hover:bg-surface-sunken rounded-lg transition-colors duration-150 flex-shrink-0"
                >
                    {isSaved ? (
                        <BookmarkSolidIcon className="h-4 w-4 text-accent" />
                    ) : (
                        <BookmarkIcon className="h-4 w-4 text-ink-faint" />
                    )}
                </button>
            </div>

            {/* Placeholder image area */}
            <div className="relative w-full h-32 bg-surface-sunken rounded-lg overflow-hidden mb-3 flex-shrink-0">
                <div className="w-full h-full flex flex-col items-center justify-center p-4">
                    <BuildingOfficeIcon className="h-10 w-10 text-ink-faint mb-2" />
                    <div className="text-center">
                        <p className="text-xs font-semibold text-ink-muted line-clamp-1">{establishment.name}</p>
                        {establishment.address?.city && (
                            <p className="text-[10px] text-ink-faint line-clamp-1 mt-0.5">{establishment.address.city}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Tags & Enrichment Level */}
            <div className="flex items-center justify-between mb-3 mt-auto">
                {(franchise || categories.length > 0 || subCategories.length > 0) ? (
                    <div className="flex flex-wrap gap-1">
                        {franchise && (
                            <span className="px-1.5 py-0.5 text-[11px] font-medium border border-border bg-surface-sunken text-ink-muted rounded-sm">
                                {franchise}
                            </span>
                        )}
                        {categories.slice(0, 2).map((tag, index) => (
                            <span key={`cat-${index}`} className="px-1.5 py-0.5 text-[11px] font-medium border border-border bg-surface-sunken text-ink-muted rounded-sm">
                                {tag}
                            </span>
                        ))}
                        {subCategories.slice(0, 1).map((tag, index) => (
                            <span key={`sub-${index}`} className="px-1.5 py-0.5 text-[11px] font-medium border border-border bg-surface-sunken text-ink-muted rounded-sm">
                                {tag}
                            </span>
                        ))}
                        {(categories.length + subCategories.length > 3) && (
                            <span className="px-1.5 py-0.5 text-[11px] text-ink-faint">
                                +{categories.length + subCategories.length - 3}
                            </span>
                        )}
                    </div>
                ) : <div />}

                <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-sm border ${
                    hasPerson
                        ? 'bg-success-subtle text-success border-success/20'
                        : 'bg-surface-sunken text-ink-muted border-border'
                }`}>
                    {enrichmentLevel}
                </span>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between text-[11px] pt-3 border-t border-border mt-auto">
                <div className="flex items-center gap-2 text-ink">
                    {signalDate && (
                        <div className="flex items-center gap-1">
                            {isRecent ? (
                                <div className="w-2 h-2 bg-success rounded-full motion-safe:animate-pulse mr-1" />
                            ) : (
                                <CalendarIcon className="h-3 w-3" />
                            )}
                            <span>{formattedDate}</span>
                            {daysUntilOpening !== null && (
                                <span className="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-medium bg-canvas text-ink border border-border rounded-sm">
                                    {daysUntilOpening === 0 ? "aujourd'hui" : `dans ${daysUntilOpening} jour${daysUntilOpening > 1 ? 's' : ''}`}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {establishment.score != null && (
                    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${
                        establishment.score >= 80 ? 'bg-success-subtle text-success' :
                        establishment.score >= 60 ? 'bg-info-subtle text-info' :
                        establishment.score >= 30 ? 'bg-warning-subtle text-warning' :
                        'bg-surface-sunken text-ink-faint'
                    }`}>
                        {establishment.score}
                    </span>
                )}
            </div>
        </div>
    );
}
